import {ProForm} from "@ant-design/pro-form";
import {ProFormCaptcha, ProFormText} from '@ant-design/pro-components';
import {Alert, Button, Form, Input, message, Modal, Spin, Tabs} from 'antd';
import React, {Component} from 'react';
import axios from '../api/axios'
import {withRouter} from "react-router";
import withRef from "../hoc/withRef";
import WechatLogin from "react-wechat-login";
import './login.css'
import {device} from 'device.js';
import MobileOutlined from "@ant-design/icons/lib/icons/MobileOutlined";
import LockOutlined from "@ant-design/icons/lib/icons/LockOutlined";
import userUtil from '../util/UserUtil';
import errorLogger, { summarizeError } from '../util/errorLogger';

const layout = {
    labelCol: {
        xs: {span: 24},
        sm: {span: 8},
    },
    wrapperCol: {
        xs: {span: 24},
        sm: {span: 16},
    },
};
const tailLayout = {
    wrapperCol: {
        xs: {span: 24, offset: 0},
        sm: {span: 16, offset: 8},
    },
};

class Login extends Component {
    qrLoginPollTimer = null;
    qrLoginPollCount = 0;
    qrLoginMessageHandler = null;
    qrLoginCompleted = false;
    wxLoginFrameRef = React.createRef();
    wxLoginIframe = null;
    wxLoginIframeLoadHandler = null;
    lastWechatCallbackErrorKey = '';

    state = {
        loading: false,
        visible: false,
        showPwd: !!(window.app && window.app.defaultPasswordLogin),
        loginType: "account",
        loginError: '',
        wechatCallbackError: false,
        wechatFrameNonce: 0
    };

    getLoginErrorMessage = (error, fallback = '登录失败，请稍后重试') => {
        const responseData = error && error.responseData;
        const responseBody = error && error.response && error.response.data;
        const status = error?.response?.status;
        const code = responseData?.code ?? responseBody?.code;
        const msg = responseData?.msg || responseData?.message || responseBody?.msg || responseBody?.message;

        // 后端明确返回了业务错误信息
        if (msg) {
            if (status && status >= 500) {
                return `服务端异常（HTTP ${status}）：${msg}。请稍后重试，若持续出现请联系管理员。`;
            }
            if (status && status >= 400) {
                return `登录请求被拒绝（HTTP ${status}）：${msg}。请检查账号密码或验证码是否正确。`;
            }
            if (code !== undefined && code !== null) {
                return `登录失败（业务码 ${code}）：${msg}`;
            }
            return `登录失败：${msg}`;
        }

        // HTTP 状态码已知但没有 msg
        if (status) {
            if (status === 401) return '登录失败：账号或密码错误，或登录状态已过期，请重新登录。';
            if (status === 403) return '登录失败：账号已被禁用或无权限访问，请联系管理员。';
            if (status === 404) return '登录失败：登录接口不存在（404），请确认服务是否正常运行。';
            if (status === 429) return '登录失败：请求过于频繁，请稍后再试。';
            if (status === 500) return '登录失败：服务端内部错误（500），请稍后重试或联系管理员。';
            if (status === 502) return '登录失败：网关错误（502），请检查后端服务是否正常运行。';
            if (status === 503) return '登录失败：服务暂时不可用（503），请稍后重试。';
            if (status >= 500) return `登录失败：服务端异常（HTTP ${status}），请稍后重试或联系管理员。`;
            if (status >= 400) return `登录失败：请求异常（HTTP ${status}），请检查输入信息后重试。`;
        }

        // 网络层错误
        if (error?.code === 'ECONNABORTED') {
            return '登录请求超时（15秒无响应），请检查网络连接或稍后重试。';
        }

        if (error?.code === 'ECONNREFUSED') {
            return '登录服务连接被拒绝，请确认后端服务是否已启动。';
        }

        if (error?.message && error.message.includes('Network Error')) {
            return '网络连接失败，请检查网络或 VPN/代理设置后重试。';
        }

        if (error?.message && error.message.includes('timeout')) {
            return '登录请求超时，请检查网络后重试。';
        }

        if (!navigator.onLine) {
            return '当前设备处于离线状态，请检查网络连接后重试。';
        }

        return fallback;
    };

    isWechatLoginBusinessError = payload => {
        if (!payload || typeof payload !== 'object') {
            return false;
        }
        const code = payload.code;
        const msg = payload.msg || payload.message;
        const token = payload.token || payload.tn;
        return !token && code !== undefined && code !== null && code !== 0 && code !== 200 && !!msg;
    };

    getWechatLoginErrorMessage = payload => {
        const code = payload && payload.code;
        const msg = payload && (payload.msg || payload.message);
        if (code !== undefined && code !== null && msg) {
            return `微信扫码登录失败（业务码 ${code}）：${msg}。请重新扫码，或切换账号密码登录。`;
        }
        if (msg) {
            return `微信扫码登录失败：${msg}。请重新扫码，或切换账号密码登录。`;
        }
        return '微信扫码登录失败，请重新扫码，或切换账号密码登录。';
    };

    parseWechatCallbackText = text => {
        if (!text || typeof text !== 'string') {
            return null;
        }
        const trimmed = text.trim();
        if (!trimmed || trimmed[0] !== '{') {
            return null;
        }
        try {
            const payload = JSON.parse(trimmed);
            return this.isWechatLoginBusinessError(payload) ? payload : null;
        } catch (e) {
            return null;
        }
    };

    componentDidMount() {
        const isLocalDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
        if (isLocalDev) {
            this.setState({ showPwd: true });
        }

        this.qrLoginMessageHandler = event => {
            const redirectUri = window.app && window.app.redirectUri ? window.app.redirectUri : '';
            let expectedOrigin = '';
            try {
                expectedOrigin = redirectUri ? new URL(redirectUri).origin : '';
            } catch (e) {
                errorLogger.error('SJ-AUTH-001', 'invalid redirectUri while handling postMessage', {
                    redirectUri,
                    error: e && e.message ? e.message : e,
                });
            }

            const loginPayload = this.normalizeWechatLoginPayload(event.data);
            if (expectedOrigin && event.origin === expectedOrigin && this.isWechatLoginBusinessError(loginPayload)) {
                const errorText = this.getWechatLoginErrorMessage(loginPayload);
                errorLogger.error('SJ-AUTH-024', 'received wechat login postMessage business error', {
                    origin: event.origin,
                    code: loginPayload.code,
                    msg: loginPayload.msg || loginPayload.message,
                    source: loginPayload.source,
                    visibleMessage: errorText,
                });
                this.reportLoginDiag('wechat-post-message-business-error', {
                    origin: event.origin,
                    code: loginPayload.code,
                    msg: loginPayload.msg || loginPayload.message,
                    source: loginPayload.source,
                });
                this.stopQrLoginPolling('wechat-post-message-business-error');
                message.error(errorText);
                this.setState({loginError: errorText, wechatCallbackError: true});
                return;
            }

            if (expectedOrigin && event.origin === expectedOrigin && loginPayload && loginPayload.token) {
                errorLogger.info('SJ-AUTH-002', 'received login postMessage payload', {
                    origin: event.origin,
                    hasToken: !!loginPayload.token,
                    hasUserNum: !!loginPayload.userNum,
                });
                this.reportLoginDiag('post-message-token', {
                    origin: event.origin,
                    hasToken: !!loginPayload.token,
                    hasUserNum: !!loginPayload.userNum,
                    validToken: userUtil.isValidAuthToken(loginPayload.token),
                });
                this.persistWechatLoginPayload(loginPayload, 'window-message');
                this.handleLoginSuccess('same-origin-postMessage');
                return;
            }

            if (!expectedOrigin || event.origin !== expectedOrigin || (event.data && event.data.source)) {
                if (expectedOrigin && event.origin !== expectedOrigin && !(event.data && event.data.source)) {
                    errorLogger.warn('SJ-AUTH-003', 'ignored postMessage origin mismatch', {
                        origin: event.origin,
                        expectedOrigin,
                        dataType: typeof event.data,
                    });
                }
                return;
            }

            errorLogger.info('SJ-AUTH-004', 'received same-origin postMessage without token', {
                origin: event.origin,
                dataType: typeof event.data,
                hasData: !!event.data,
                hasToken: !!(loginPayload && loginPayload.token),
                hasUserNum: !!(loginPayload && loginPayload.userNum),
            });
        };
        window.addEventListener('message', this.qrLoginMessageHandler);
        window.setTimeout(this.bindWechatIframeWatcher, 0);
    }

    componentDidUpdate(prevProps, prevState) {
        const shouldPoll = this.state.visible && !this.state.showPwd;
        const wasPolling = prevState.visible && !prevState.showPwd;

        if (shouldPoll && !wasPolling) {
            this.startQrLoginPolling('modal-open');
        }

        if (!shouldPoll && wasPolling) {
            this.stopQrLoginPolling('modal-hidden-or-mode-changed');
            this.detachWechatIframeWatcher();
        }

        if (shouldPoll) {
            window.setTimeout(this.bindWechatIframeWatcher, 0);
        }
    }

    show(onLogin) {
        this.qrLoginCompleted = false;
        errorLogger.info('SJ-AUTH-005', 'show login modal', {
            pathname: window.location.pathname,
            search: window.location.search,
            appId: window.app && window.app.appId,
            redirectUri: window.app && window.app.redirectUri,
        });
        this.reportLoginDiag('show-login-modal', {
            pathname: window.location.pathname,
            search: window.location.search,
        });
        this.setState({visible: true, onLogin: onLogin, loginError: '', wechatCallbackError: false})
    }

    hide = () => {
        this.setState({visible: false, onLogin: null, loginError: '', wechatCallbackError: false});
    };

    componentWillUnmount = () => {
        this.stopQrLoginPolling('component-unmount');
        this.detachWechatIframeWatcher();
        if (this.qrLoginMessageHandler) {
            window.removeEventListener('message', this.qrLoginMessageHandler);
            this.qrLoginMessageHandler = null;
        }
        this.setState = (state, callback) => {
        };
    };

    detachWechatIframeWatcher = () => {
        if (this.wxLoginIframe && this.wxLoginIframeLoadHandler) {
            this.wxLoginIframe.removeEventListener('load', this.wxLoginIframeLoadHandler);
        }
        this.wxLoginIframe = null;
        this.wxLoginIframeLoadHandler = null;
    };

    bindWechatIframeWatcher = () => {
        const container = this.wxLoginFrameRef && this.wxLoginFrameRef.current;
        if (!container) {
            return;
        }
        const iframe = container.querySelector('iframe');
        if (!iframe || iframe === this.wxLoginIframe) {
            return;
        }

        this.detachWechatIframeWatcher();
        this.wxLoginIframe = iframe;
        this.wxLoginIframeLoadHandler = () => this.inspectWechatIframeCallback('iframe-load');
        iframe.addEventListener('load', this.wxLoginIframeLoadHandler);
        this.inspectWechatIframeCallback('iframe-bind');
    };

    inspectWechatIframeCallback = reason => {
        const iframe = this.wxLoginIframe;
        if (!iframe) {
            return;
        }

        let payload = null;
        let callbackUrl = '';
        try {
            callbackUrl = iframe.contentWindow && iframe.contentWindow.location
                ? iframe.contentWindow.location.href
                : iframe.src;
            const doc = iframe.contentDocument || (iframe.contentWindow && iframe.contentWindow.document);
            const bodyText = doc && doc.body ? doc.body.textContent : '';
            payload = this.parseWechatCallbackText(bodyText);
        } catch (e) {
            return;
        }

        if (!payload) {
            return;
        }

        const errorKey = `${payload.code}:${payload.msg || payload.message}:${callbackUrl}`;
        if (errorKey === this.lastWechatCallbackErrorKey) {
            return;
        }
        this.lastWechatCallbackErrorKey = errorKey;

        const errorText = this.getWechatLoginErrorMessage(payload);
        const callbackInfo = {};
        try {
            const url = new URL(callbackUrl);
            callbackInfo.host = url.host;
            callbackInfo.path = url.pathname;
            callbackInfo.appId = url.searchParams.get('appId') || '';
            callbackInfo.hasCode = !!url.searchParams.get('code');
            callbackInfo.state = url.searchParams.get('state') || '';
        } catch (e) { /* ignore */ }

        errorLogger.error('SJ-AUTH-023', 'wechat iframe callback returned business error', {
            reason,
            visibleMessage: errorText,
            code: payload.code,
            msg: payload.msg || payload.message,
            callback: callbackInfo,
        });
        this.reportLoginDiag('wechat-callback-business-error', {
            reason,
            code: payload.code,
            msg: payload.msg || payload.message,
            callback: callbackInfo,
        });
        this.stopQrLoginPolling('wechat-callback-business-error');
        message.error(errorText);
        this.setState({loginError: errorText, wechatCallbackError: true});
    };

    refreshWechatLoginFrame = () => {
        this.lastWechatCallbackErrorKey = '';
        this.detachWechatIframeWatcher();
        this.setState(prevState => ({
            loginError: '',
            wechatCallbackError: false,
            wechatFrameNonce: prevState.wechatFrameNonce + 1,
        }), () => {
            this.startQrLoginPolling('refresh-wechat-frame');
            window.setTimeout(this.bindWechatIframeWatcher, 0);
        });
    };

    reportLoginDiag = (eventName, detail = {}) => {
        try {
            const stored = userUtil.getStoredLogin();
            const body = JSON.stringify({
                eventName,
                detail,
                href: window.location.href,
                appId: window.app && window.app.appId,
                biz: window.app && window.app.biz,
                redirectUri: window.app && window.app.redirectUri,
                hasStoredToken: !!(stored && stored.token),
                storedUserNum: stored && stored.userNum,
                userAgent: window.navigator && window.navigator.userAgent,
                ts: Date.now(),
            });
            if (navigator.sendBeacon) {
                navigator.sendBeacon('/api/pub/login/diag', new Blob([body], {type: 'application/json'}));
            } else {
                fetch('/api/pub/login/diag', {
                    method: 'POST',
                    headers: {'Content-Type': 'application/json'},
                    body,
                    keepalive: true,
                }).catch(() => {});
            }
        } catch (e) { /* ignore */ }
    };

    handleLoginSuccess = reason => {
        if (this.qrLoginCompleted) {
            errorLogger.info('SJ-AUTH-006', 'duplicate login success ignored', {reason});
            return;
        }
        this.qrLoginCompleted = true;
        const {onLogin} = this.state;

        errorLogger.info('SJ-AUTH-007', 'login success detected', {
            reason,
            pathname: window.location.pathname,
            search: window.location.search,
        });
        this.reportLoginDiag('login-success', {reason});

        this.stopQrLoginPolling(`success:${reason}`);
        this.setState({loading: false, visible: false, onLogin: null});

        if (onLogin) {
            onLogin();
        } else {
            window.location.reload();
        }
    };

    normalizeWechatLoginPayload = payload => {
        if (!payload) {
            return null;
        }

        if (typeof payload === 'string') {
            try {
                return JSON.parse(payload);
            } catch (e) {
                errorLogger.warn('SJ-AUTH-008', 'unable to parse postMessage payload', {
                    payloadLength: payload.length,
                    error: e && e.message ? e.message : e,
                });
                return null;
            }
        }

        if (typeof payload === 'object') {
            return payload;
        }

        return null;
    };

    persistWechatLoginPayload = (payload, reason) => {
        if (!payload) {
            errorLogger.error('SJ-AUTH-009a', 'login payload is null or undefined', { reason });
            return false;
        }

        const payloadType = typeof payload;
        const payloadKeys = Object.keys(payload);
        const userNum = payload.userNum || payload.userId || payload.un;
        const token = payload.token || payload.tn;

        errorLogger.info('SJ-AUTH-009', 'persist login payload', {
            reason,
            payloadType,
            payloadKeys,
            hasUserNum: !!userNum,
            hasToken: !!token,
        });

        // 分别检测缺少的字段，给出更精确的错误
        if (!userNum && !token) {
            errorLogger.error('SJ-AUTH-010a', 'login payload missing both token and userNum', {
                reason,
                payloadType,
                payloadKeys,
            });
            this.reportLoginDiag('persist-missing-token-and-user', {
                reason,
                payloadKeys,
            });
            return false;
        }

        if (!userNum) {
            errorLogger.error('SJ-AUTH-010b', 'login payload missing userNum', {
                reason,
                payloadType,
                payloadKeys,
                hasToken: !!token,
            });
            this.reportLoginDiag('persist-missing-user', {
                reason,
                payloadKeys,
            });
            return false;
        }

        if (!token) {
            errorLogger.error('SJ-AUTH-010c', 'login payload missing token', {
                reason,
                payloadType,
                payloadKeys,
                userNum,
            });
            this.reportLoginDiag('persist-missing-token', {
                reason,
                payloadKeys,
            });
            return false;
        }

        const saved = userUtil.saveLogin(userNum, token);
        this.reportLoginDiag('persist-login-payload', {
            reason,
            userNum,
            hasToken: !!token,
            validToken: userUtil.isValidAuthToken(token),
            saved,
        });
        return saved;
    };

    isCheckLoginSuccess = res => {
        if (res === true) {
            return true;
        }

        if (!res || typeof res !== 'object') {
            return false;
        }

        return res.code !== 401 && (
            res.userNum ||
            res.token ||
            res.code === 0 ||
            res.data === true
        );
    };

    startQrLoginPolling = reason => {
        if (this.qrLoginPollTimer) {
            return;
        }

        this.qrLoginPollCount = 0;
        errorLogger.info('SJ-AUTH-011', 'start qr login polling', {
            reason,
            appId: window.app && window.app.appId,
            redirectUri: window.app && window.app.redirectUri,
        });

        const poll = () => {
            this.qrLoginPollCount += 1;
            const attempt = this.qrLoginPollCount;

            axios.get('/api/wx/checkLogin', {
                params: { _wxPoll: attempt },
                skipLoginModal: true,
            }).then(res => {
                const loggedIn = this.isCheckLoginSuccess(res);
                errorLogger.info('SJ-AUTH-012', 'qr poll result', {
                    attempt,
                    loggedIn,
                    code: res && res.code,
                    value: typeof res === 'boolean' ? res : undefined,
                    hasUserNum: !!(res && res.userNum),
                    hasToken: !!(res && res.token),
                    responseType: typeof res,
                });
                if (loggedIn || attempt === 1 || attempt === 5 || attempt === 15 || attempt === 30) {
                    this.reportLoginDiag('check-login-poll', {
                        attempt,
                        loggedIn,
                        code: res && res.code,
                        responseType: typeof res,
                    });
                }

                if (loggedIn) {
                    const saved = this.persistWechatLoginPayload(res, 'checkLogin-poll');
                    const stored = userUtil.getStoredLogin();
                    if (saved || (stored && stored.token)) {
                        this.handleLoginSuccess('checkLogin-poll');
                    }
                }
            }).catch(error => {
                const errorText = this.getLoginErrorMessage(error, '扫码登录状态检查失败，请刷新二维码或切换账号密码登录');
                errorLogger.error('SJ-AUTH-013', 'qr poll failed', {
                    attempt,
                    visibleMessage: errorText,
                    ...summarizeError(error),
                });
                this.setState({loginError: errorText});
            });

            if (attempt >= 60) {
                this.stopQrLoginPolling('poll-timeout');
                const errorText = '二维码登录已超时，请刷新页面重新扫码，或切换账号密码登录。';
                errorLogger.warn('SJ-AUTH-014', 'qr login polling timed out', {attempt});
                this.setState({loginError: errorText});
            }
        };

        poll();
        this.qrLoginPollTimer = window.setInterval(poll, 2000);
    };

    stopQrLoginPolling = reason => {
        if (!this.qrLoginPollTimer) {
            return;
        }

        window.clearInterval(this.qrLoginPollTimer);
        this.qrLoginPollTimer = null;
        errorLogger.info('SJ-AUTH-015', 'stop qr login polling', {
            reason,
            attempts: this.qrLoginPollCount,
        });
    };

    onFinish = values => {
        this.setState({loginError: ''});
        errorLogger.info('SJ-AUTH-016', 'account login submit', {
            userId: values && values.userId,
            hasPassword: !!(values && values.password),
        });
        this.setState({loading: true})
        const loginParams = {
            channel: 'app',
            platform: 'pc',
            appId: window.app && window.app.serviceAppId ? window.app.serviceAppId : window.app.appId,
            biz: window.app && window.app.biz ? window.app.biz : 'time'
        };
        if (window.app && window.app.agentAuthInstall) {
            loginParams.agentAuth = '1';
        }
        const doLogin = (payload) => {
            axios.post('/api/login/doLogin', payload, {
                params: loginParams,
                rejectOnBusinessError: true
            }).then(data => {
                const {onLogin} = this.state;

                // 防御：接口返回空值（通常是后端未启动或代理返回了空 body）
                if (!data) {
                    const isLocalDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
                    const errorText = isLocalDev
                        ? '登录请求未收到有效响应，请确认后端服务是否已启动（检查 localhost 服务端口或代理配置）。'
                        : '登录接口返回了空数据，无法完成登录。请稍后重试，若持续出现请联系管理员。';
                    errorLogger.error('SJ-AUTH-017a', 'account login response is empty', {
                        userId: payload && payload.userId,
                        hostname: window.location.hostname,
                    });
                    message.error(errorText);
                    this.setState({loading: false, loginError: errorText});
                    return;
                }

                // 防御：接口返回了错误码
                if (data.code !== undefined && data.code !== 0 && data.code !== 200) {
                    const errorText = data.msg || data.message || `登录失败（业务码 ${data.code}），请检查账号密码后重试。`;
                    errorLogger.warn('SJ-AUTH-017b', 'account login response has business error code', {
                        code: data.code,
                        msg: data.msg || data.message,
                        userId: payload && payload.userId,
                    });
                    message.error(errorText);
                    this.setState({loading: false, loginError: errorText});
                    return;
                }

                const dataKeys = Object.keys(data);
                const userNum = data.userNum || data.userId || data.un;
                const token = data.token || data.tn;
                const cookieLogin = data.cookieLogin === true;

                errorLogger.info('SJ-AUTH-017', 'account login response', {
                    dataKeys,
                    hasUserNum: !!userNum,
                    hasToken: !!token,
                    cookieLogin,
                });

                if (cookieLogin && (!userNum || !token) && userUtil.getStoredLogin()?.token) {
                    errorLogger.info('SJ-AUTH-017c', 'account login succeeded via response cookie', {
                        dataKeys,
                        hasUserNum: !!userNum,
                        hasToken: !!token,
                    });
                    this.setState({loading: false, visible: false, onLogin: null});
                    if (onLogin) {
                        onLogin();
                    } else {
                        window.location.reload();
                    }
                    return;
                }

                if (!userNum || !token) {
                    let missingFields = [];
                    if (!userNum) missingFields.push('用户编号（userNum / userId / un）');
                    if (!token) missingFields.push('登录凭证（token / tn）');

                    const errorText = `登录接口返回了数据，但缺少 ${missingFields.join(' 和 ')}。` +
                        `接口实际返回的字段：${dataKeys.length > 0 ? dataKeys.join('、') : '（无字段）'}。` +
                        `请联系管理员检查登录接口返回数据结构。`;

                    errorLogger.warn('SJ-AUTH-018', 'account login response missing required fields', {
                        dataKeys,
                        missingFields,
                        dataType: typeof data,
                    });
                    message.error(errorText);
                    this.setState({loading: false, loginError: errorText});
                    return;
                }
                this.setState({loading: false, visible: false, onLogin: null})
                userUtil.saveLogin(userNum, token);
                if (onLogin) {
                    onLogin();
                } else {
                    window.location.reload()
                }
            }).catch(error => {
                const errorText = this.getLoginErrorMessage(error, '账号或密码登录失败，请检查账号密码后重试');
                errorLogger.error('SJ-AUTH-019', 'account login failed', {
                    visibleMessage: errorText,
                    ...summarizeError(error),
                });
                message.error(errorText);
                this.setState({loading: false, loginError: errorText});
            });
        };
        doLogin(values);
    };
    codeFormRef = React.createRef();
    onFinishFailed = errorInfo => {
        const firstError = errorInfo && errorInfo.errorFields && errorInfo.errorFields[0];
        const errorText = firstError && firstError.errors && firstError.errors[0]
            ? firstError.errors[0]
            : '请先补全登录表单中的必填项';
        errorLogger.warn('SJ-AUTH-020', 'login form validation failed', {
            values: errorInfo && errorInfo.values,
            errorFields: errorInfo && errorInfo.errorFields,
        });
        this.setState({loginError: errorText});
    };

    render() {
        const {loginType, loginError} = this.state;
        return (
            <Modal
                title={device.mobile ? "不支持手机访问" : "登录"}
                footer={null}
                className="sj-login-modal"
                onCancel={this.hide}
                open={this.state.visible}>
                {device.mobile && <div>
                    请使用电脑浏览器打开本站：${window.location.href}
                </div>}
                {!device.mobile &&
                <Spin spinning={this.state.loading} className={"loginSpin"}>
                    {loginError && (
                        <Alert
                            className="loginErrorAlert"
                            type="error"
                            showIcon
                            message={loginError}
                        />
                    )}
                    {this.state.showPwd &&
                    <div>

                        <Tabs
                            activeKey={loginType}
                            items={[
                                {key: 'account', label: '账号密码登录'},
                                {key: 'phone', label: '手机号登录'},
                            ]}
                            onChange={(activeKey) => {
                            this.setState({
                                loginType: activeKey,
                                loginError: '',
                                wechatCallbackError: false
                            });

                        }}>
                        </Tabs>
                        {loginType === 'account' && (
                            <Form
                                {...layout}
                                name="basic"
                                initialValues={{
                                    remember: true,
                                }}
                                onFinish={this.onFinish}
                                onFinishFailed={this.onFinishFailed}
                            >
                                <Form.Item
                                    label="用户名/手机号/邮箱"
                                    name="userId"
                                    rules={[
                                        {
                                            required: true,
                                            message: '请输入用户名/手机号/邮箱',
                                        },
                                    ]}
                                >
                                    <Input/>
                                </Form.Item>

                                <Form.Item
                                    label="密码"
                                    name="password"
                                    rules={[
                                        {
                                            required: true,
                                            message: '请输入密码',
                                        },
                                    ]}
                                >
                                    <Input.Password/>
                                </Form.Item>


                                <Form.Item {...tailLayout}>
                                    <Button type="primary" htmlType="submit">
                                        登录
                                    </Button>
                                </Form.Item>
                            </Form>
                        )}
                        {loginType === 'phone' && (
                            <div>
                                <ProForm
                                    {...layout}
                                    name="basic"
                                    initialValues={{}}
                                    formRef={this.codeFormRef}
                                    onFinish={this.onFinish}
                                    onFinishFailed={this.onFinishFailed}
                                >

                                    <ProFormText
                                        fieldProps={{
                                            size: 'large',
                                            prefix: <MobileOutlined className={'prefixIcon'}/>,
                                        }}
                                        name="phone"
                                        placeholder={'手机号'}
                                        rules={[
                                            {
                                                required: true,
                                                message: '请输入手机号！',
                                            },
                                            {
                                                pattern: /^1\d{10}$/,
                                                message: '手机号格式错误！',
                                            },
                                        ]}
                                    />
                                    <ProFormCaptcha
                                        fieldProps={{
                                            size: 'large',
                                            prefix: <LockOutlined className={'prefixIcon'}/>,
                                        }}
                                        captchaProps={{
                                            size: 'large',
                                        }}
                                        placeholder={'请输入验证码'}
                                        captchaTextRender={(timing, count) => {
                                            if (timing) {
                                                return `${count} ${'获取验证码'}`;
                                            }
                                            return '获取验证码';
                                        }}
                                        name="code"
                                        rules={[
                                            {
                                                required: true,
                                                message: '请输入验证码！',
                                            },
                                        ]}
                                        onGetCaptcha={async () => {
                                            this.setState({loginError: ''});
                                            return axios.post('/api/login/sendSms', {
                                                phone: this.codeFormRef.current.getFieldFormatValue()['phone'],
                                                smsType: "login"
                                            }).then(data => {
                                                if (data) {
                                                    message.success('验证码已发送到您的手机');
                                                }
                                            }).catch(error => {
                                                const errorText = this.getLoginErrorMessage(error, '验证码发送失败，请检查手机号或稍后重试');
                                                errorLogger.error('SJ-AUTH-022', 'send sms captcha failed', {
                                                    visibleMessage: errorText,
                                                    ...summarizeError(error),
                                                });
                                                this.setState({loginError: errorText});
                                                throw error;
                                            });

                                        }}
                                    />

                                </ProForm>
                            </div>
                        )}
                    </div>}
                    {!this.state.showPwd && (() => {
                        // 开发模式下隐藏微信登录 iframe，避免 localhost redirect_uri 报错
                        try {
                            const CONFIG = require('../../config').default;
                            if (CONFIG.DEV_MODE) return null;
                        } catch (e) { /* ignore */ }
                        return <div className={'wxLogin'}>
                            {this.state.wechatCallbackError ? (
                                <div className="wxLoginErrorPanel">
                                    <Alert
                                        className="wxLoginErrorPanelAlert"
                                        type="error"
                                        showIcon
                                        message="微信扫码登录失败"
                                    />
                                    <Button size="small" onClick={this.refreshWechatLoginFrame}>刷新二维码</Button>
                                </div>
                            ) : <div className="wxLoginFrameClip" ref={this.wxLoginFrameRef}>
                                <WechatLogin
                                    key={this.state.wechatFrameNonce}
                                    appid={window.app.appId}
                                    redirectUri={window.app.redirectUri}
                                    css={`${window.location.origin}/wechat-qr-login.css`}
                                    style={{
                                        width: '322px',
                                        height: '400px',
                                        border: 0,
                                        marginLeft: '-11px'
                                    }}
                                    onSuccess={(res) => {
                                        const payload = this.normalizeWechatLoginPayload(res);

                                        if (!payload) {
                                            const errorText = '微信扫码回调返回的数据格式不正确（无法解析为对象），无法完成登录。请重新扫码，若仍失败请联系管理员。';
                                            errorLogger.error('SJ-AUTH-021a', 'wechat iframe login callback payload is invalid', {
                                                resType: typeof res,
                                                resValue: typeof res === 'string' ? res.slice(0, 200) : undefined,
                                            });
                                            message.error(errorText);
                                            this.stopQrLoginPolling('wechat-onSuccess-business-error');
                                            this.setState({loginError: errorText, wechatCallbackError: true});
                                            return;
                                        }

                                        const payloadKeys = Object.keys(payload);
                                        const userNum = payload.userNum || payload.userId || payload.un;
                                        const token = payload.token || payload.tn;

                                        if (this.isWechatLoginBusinessError(payload)) {
                                            const errorText = this.getWechatLoginErrorMessage(payload);
                                            errorLogger.error('SJ-AUTH-021b', 'wechat login callback returned business error payload', {
                                                code: payload.code,
                                                msg: payload.msg || payload.message,
                                                payloadKeys,
                                                visibleMessage: errorText,
                                            });
                                            this.reportLoginDiag('wechat-onSuccess-business-error', {
                                                code: payload.code,
                                                msg: payload.msg || payload.message,
                                                payloadKeys,
                                            });
                                            message.error(errorText);
                                            this.stopQrLoginPolling('wechat-onSuccess-business-error');
                                            this.setState({loginError: errorText, wechatCallbackError: true});
                                            return;
                                        }

                                        errorLogger.info('SJ-AUTH-021', 'wechat iframe login success callback', {
                                            payloadKeys,
                                            hasToken: !!token,
                                            hasUserNum: !!userNum,
                                        });

                                        if (this.persistWechatLoginPayload(payload, 'react-wechat-login')) {
                                            this.handleLoginSuccess('iframe-postMessage');
                                        } else {
                                            let missingFields = [];
                                            if (!userNum) missingFields.push('用户编号');
                                            if (!token) missingFields.push('登录凭证');
                                            const errorText = `微信扫码登录成功，但返回的数据缺少 ${missingFields.join(' 和 ')}。` +
                                                `接口返回的字段：${payloadKeys.length > 0 ? payloadKeys.join('、') : '（无字段）'}。` +
                                                `请重新扫码，若仍失败请联系管理员检查微信登录接口。`;
                                            message.error(errorText);
                                            this.setState({loginError: errorText});
                                        }
                                    }}
                                />
                            </div>}
                        </div>;
                    })()}
                    <div>
                        <a onClick={() => {
	                            this.setState({
	                                showPwd: !this.state.showPwd,
	                                loginError: ''
	                            })
                        }} className={"changeLoginBtn"}>{this.state.showPwd ? "微信扫码登录" : "账号密码登录"}</a>
                    </div>
                </Spin>}
            </Modal>
        );
    }

};
export default withRouter(withRef(Login))
